/**
 * shadcn/Tailwind replacement for MarkdownEditorWithPreview.
 * Provides split/editor/preview tabs with a clean toolbar for markdown editing.
 */

import React, { useState } from 'react';
import { Maximize2, Minimize2, Eye, Code2, Columns2 } from 'lucide-react';
import { FieldLabelWithRequired } from '../entities/renderDynamicField';
import { ScimAceEditor } from './ScimAceEditor';
import { ShadcnMarkdownPreview } from './ShadcnMarkdownPreview';
import { useContentBodyDeepLink } from '../../hooks/useContentBodyDeepLink';
import { cn } from '../../lib/utils';

export interface ShadcnContentEditorProps {
    value: string;
    onChange?: (value: string) => void;
    label?: string;
    isRequired?: boolean;
    loading?: boolean;
    disabled?: boolean;
    height?: string;
    name?: string;
    contentBodyDeepLink?: boolean;
    onContentLinkClick?: (entityId: string, displayName: string) => void;
}

type EditorTab = 'split' | 'editor' | 'preview';

export const ShadcnContentEditor: React.FC<ShadcnContentEditorProps> = ({
    value,
    onChange,
    label,
    isRequired = false,
    loading = false,
    disabled = false,
    height = '500px',
    name = 'markdown-editor',
    contentBodyDeepLink = false,
    onContentLinkClick,
}) => {
    const [activeTab, setActiveTab] = useState<EditorTab>(disabled ? 'preview' : 'split');
    const { isFullscreen, toggleFullscreen } = useContentBodyDeepLink(contentBodyDeepLink);
    const [localFullscreen, setLocalFullscreen] = useState(false);
    const isFullscreenEffective = contentBodyDeepLink ? isFullscreen : localFullscreen;

    const handleFullscreenToggle = () => {
        if (contentBodyDeepLink) {
            toggleFullscreen();
        } else {
            setLocalFullscreen((f) => !f);
        }
    };

    const editorHeight = isFullscreenEffective ? 'calc(100vh - 200px)' : height;
    const isReadOnly = loading || disabled;

    const tabs: { id: EditorTab; label: string; icon: React.FC<any> }[] = [
        { id: 'split', label: 'Split', icon: Columns2 },
        { id: 'editor', label: 'Editor', icon: Code2 },
        { id: 'preview', label: 'Preview', icon: Eye },
    ];

    return (
        <div
            className={cn(
                'mb-3',
                isFullscreenEffective && 'fixed inset-0 z-[9999] bg-background p-4',
            )}
        >
            {/* Header with label + fullscreen */}
            <div className="flex items-center justify-between mb-2">
                {label && (
                    <span className="text-sm font-medium text-foreground">
                        <FieldLabelWithRequired label={label} required={isRequired} readOnly={isReadOnly} />
                    </span>
                )}
                <button
                    onClick={handleFullscreenToggle}
                    className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title={isFullscreenEffective ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                    {isFullscreenEffective ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
            </div>

            {/* Editor card */}
            <div
                className={cn(
                    'rounded-lg border border-border bg-card overflow-hidden',
                    isFullscreenEffective && 'h-[calc(100vh-100px)]',
                )}
            >
                {/* Tab bar */}
                <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
                    {tabs.map(({ id, label: tabLabel, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={cn(
                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                                activeTab === id
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                            )}
                        >
                            <Icon size={14} />
                            {tabLabel}
                        </button>
                    ))}
                </div>

                {/* Split view */}
                {activeTab === 'split' && (
                    <div className="flex" style={{ height: editorHeight }}>
                        <div className="flex-1 border-r border-border">
                            <ScimAceEditor
                                mode="markdown"
                                value={value || ''}
                                onChange={onChange}
                                readOnly={isReadOnly}
                                height={editorHeight}
                                name={`${name}-split-editor`}
                                fontSize={14}
                                showPrintMargin={false}
                            />
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <ShadcnMarkdownPreview
                                content={value || ''}
                                emptyMessage="Start typing markdown to see preview..."
                                onContentLinkClick={onContentLinkClick}
                                className="border-0 p-0"
                            />
                        </div>
                    </div>
                )}

                {/* Editor only */}
                {activeTab === 'editor' && (
                    <ScimAceEditor
                        mode="markdown"
                        value={value || ''}
                        onChange={onChange}
                        readOnly={isReadOnly}
                        height={editorHeight}
                        name={`${name}-editor`}
                        fontSize={14}
                        showPrintMargin={false}
                    />
                )}

                {/* Preview only */}
                {activeTab === 'preview' && (
                    <div className="overflow-auto p-4" style={{ height: editorHeight }}>
                        <ShadcnMarkdownPreview
                            content={value || ''}
                            emptyMessage="No content to preview"
                            onContentLinkClick={onContentLinkClick}
                            className="border-0 p-0"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
